// src/pages/Users/Galleries/RemovePhotoFromAlbumModal.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaImage,
  FaSearch,
  FaTimes,
  FaTrash,
} from "react-icons/fa";
import { toast } from "react-toastify";

import {
  getPhotosByGalleryId,
  deletePhoto,
} from "../../../services/photoServices";

import { toAbsolutePhotoUrl as toAbsPhoto } from "../../../services/albumPhotoServices";

const FALLBACK = "/img/no-image.jpg";

function stripHtml(html = "") {
  if (!html) return "";

  const div = document.createElement("div");
  div.innerHTML = html;

  return (div.textContent || div.innerText || "").trim();
}

function getOrderedPhotos(photos = []) {
  return [...photos].sort((a, b) => {
    const positionA = Number.isFinite(Number(a?.position))
      ? Number(a.position)
      : Number.MAX_SAFE_INTEGER;

    const positionB = Number.isFinite(Number(b?.position))
      ? Number(b.position)
      : Number.MAX_SAFE_INTEGER;

    return positionA - positionB || Number(a?.id || 0) - Number(b?.id || 0);
  });
}

export default function RemovePhotoFromAlbumModal({
  isOpen,
  onClose,
  galleryId,
}) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const searchRef = useRef(null);

  const loadPhotos = useCallback(async () => {
    if (!galleryId) return;

    setLoading(true);

    try {
      const data = await getPhotosByGalleryId(galleryId);
      const ordered = getOrderedPhotos(Array.isArray(data) ? data : []);

      setPhotos(ordered);
      setSelectedId(ordered[0]?.id ? String(ordered[0].id) : "");
      setConfirmDelete(false);
    } catch (error) {
      console.error("Erreur chargement photos :", error);
      toast.error("Impossible de charger les photos de l’album.");
      setPhotos([]);
      setSelectedId("");
    } finally {
      setLoading(false);
    }
  }, [galleryId]);

  const safeClose = useCallback(() => {
    if (deleting) return;
    onClose?.();
  }, [deleting, onClose]);

  useEffect(() => {
    if (!isOpen) return;

    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") safeClose();
    };

    window.addEventListener("keydown", handleKeyDown);

    const timer = setTimeout(() => {
      searchRef.current?.focus();
    }, 80);

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      window.removeEventListener("keydown", handleKeyDown);
      clearTimeout(timer);
    };
  }, [isOpen, safeClose]);

  useEffect(() => {
    if (!isOpen) return;

    setSearch("");
    setConfirmDelete(false);
    loadPhotos();
  }, [isOpen, loadPhotos]);

  const filteredPhotos = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) return photos;

    return photos.filter((photo) => {
      const caption = stripHtml(photo?.description || "").toLowerCase();
      const id = String(photo?.id || "");
      const position = String(photo?.position || "");

      return (
        caption.includes(query) ||
        id.includes(query) ||
        position.includes(query)
      );
    });
  }, [photos, search]);

  const selectedPhoto = useMemo(() => {
    return (
      photos.find((photo) => String(photo.id) === String(selectedId)) || null
    );
  }, [photos, selectedId]);

  const selectedPreviewUrl = toAbsPhoto(selectedPhoto?.photo_url || "");
  const selectedCaption = stripHtml(selectedPhoto?.description || "");

  const canDelete = !!selectedPhoto && confirmDelete && !deleting;

  if (!isOpen) return null;

  const handleDelete = async () => {
    if (!selectedPhoto) {
      toast.warn("Choisis une photo à supprimer.");
      return;
    }

    if (!confirmDelete) {
      toast.warn("Coche la confirmation avant de supprimer.");
      return;
    }

    try {
      setDeleting(true);

      await deletePhoto(Number(selectedPhoto.id));

      toast.success("Photo supprimée avec succès.");
      onClose?.();
    } catch (error) {
      console.error("Erreur suppression photo :", error);
      toast.error("Erreur lors de la suppression de la photo.");
    } finally {
      setDeleting(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-slate-950/70 p-3 backdrop-blur-sm sm:flex sm:items-center sm:justify-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Supprimer une photo"
      onClick={(event) => {
        if (event.target === event.currentTarget) safeClose();
      }}
    >
      <article
        className="absolute inset-x-0 bottom-0 flex max-h-[94dvh] flex-col overflow-hidden rounded-t-[2rem] bg-white shadow-2xl ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800 sm:relative sm:inset-auto sm:w-full sm:max-w-5xl sm:rounded-[2rem]"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="relative overflow-hidden bg-gradient-to-r from-rose-700 via-pink-700 to-orange-600 px-5 py-5 text-white sm:px-6">
          <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-white/15 blur-3xl" />

          <div className="relative flex items-start justify-between gap-4">
            <div className="min-w-0">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] ring-1 ring-white/20">
                <FaTrash />
                Suppression
              </span>

              <h2 className="mt-3 text-2xl font-bold leading-tight">
                Retirer une photo de l’album
              </h2>

              <p className="mt-1 text-sm leading-6 text-white/80">
                Sélectionne la photo à supprimer. Cette action est définitive.
              </p>
            </div>

            <button
              type="button"
              onClick={safeClose}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white backdrop-blur transition hover:bg-white/25 active:scale-95"
              aria-label="Fermer"
              title="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-5 sm:p-6">
          {loading ? (
            <div className="grid gap-5 lg:grid-cols-[1fr_0.9fr]">
              <div className="space-y-3">
                <div className="h-12 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div
                      key={index}
                      className="aspect-[4/3] animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800"
                    />
                  ))}
                </div>
              </div>

              <div className="aspect-[16/10] animate-pulse rounded-[1.5rem] bg-slate-200 dark:bg-slate-800" />
            </div>
          ) : photos.length === 0 ? (
            <div className="rounded-[2rem] border border-dashed border-slate-300 bg-slate-50 p-10 text-center dark:border-slate-700 dark:bg-slate-950">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-white text-slate-400 shadow-sm dark:bg-slate-900">
                <FaImage className="text-2xl" />
              </div>

              <h3 className="mt-4 text-xl font-bold text-slate-950 dark:text-white">
                Aucune photo dans cet album
              </h3>

              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                Il n’y a rien à supprimer pour le moment.
              </p>
            </div>
          ) : (
            <div className="grid gap-5 lg:grid-cols-[1.05fr_0.95fr]">
              <section>
                <div className="relative">
                  <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm text-slate-400" />

                  <input
                    ref={searchRef}
                    type="text"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Rechercher par légende, id, position..."
                    className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-11 pr-4 text-sm font-medium text-slate-900 outline-none transition focus:border-rose-400 focus:bg-white focus:ring-4 focus:ring-rose-400/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  />
                </div>

                <div className="mt-4 grid max-h-[48dvh] grid-cols-2 gap-3 overflow-y-auto pr-1 sm:grid-cols-3 lg:max-h-[56dvh]">
                  {filteredPhotos.map((photo, index) => {
                    const url = toAbsPhoto(photo?.photo_url || "");
                    const caption = stripHtml(photo?.description || "");
                    const active = String(photo.id) === String(selectedId);

                    return (
                      <button
                        key={photo.id || index}
                        type="button"
                        onClick={() => {
                          setSelectedId(String(photo.id));
                          setConfirmDelete(false);
                        }}
                        className={`group overflow-hidden rounded-2xl border bg-slate-100 text-left shadow-sm transition active:scale-[0.98] dark:bg-slate-950 ${
                          active
                            ? "border-rose-400 ring-4 ring-rose-400/15"
                            : "border-slate-200 hover:border-rose-200 dark:border-slate-800 dark:hover:border-rose-900"
                        }`}
                      >
                        <div className="relative aspect-[4/3] w-full">
                          <img
                            src={url || FALLBACK}
                            alt={caption || `Photo ${index + 1}`}
                            className="h-full w-full object-cover"
                            loading="lazy"
                            onError={(event) => {
                              event.currentTarget.src = FALLBACK;
                            }}
                          />

                          {active && (
                            <div className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-rose-600 text-white shadow">
                              <FaCheckCircle />
                            </div>
                          )}

                          <div className="absolute bottom-2 left-2 rounded-full bg-slate-950/75 px-2 py-1 text-[10px] font-bold text-white backdrop-blur">
                            #{photo.position || index + 1}
                          </div>
                        </div>

                        <div className="p-2">
                          <p className="line-clamp-2 text-xs leading-5 text-slate-600 dark:text-slate-300">
                            {caption || "Sans légende"}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {filteredPhotos.length === 0 && (
                  <div className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm font-semibold text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-400">
                    Aucune photo ne correspond à la recherche.
                  </div>
                )}
              </section>

              <section className="space-y-4">
                <div className="overflow-hidden rounded-[1.5rem] border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950">
                  <div className="aspect-[16/10] w-full bg-slate-100 dark:bg-slate-900">
                    <img
                      src={selectedPreviewUrl || FALLBACK}
                      alt={selectedCaption || "Aperçu de la photo"}
                      className="h-full w-full object-contain"
                      onError={(event) => {
                        event.currentTarget.src = FALLBACK;
                      }}
                    />
                  </div>

                  <div className="border-t border-slate-200 p-4 dark:border-slate-800">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                      Photo sélectionnée
                    </p>

                    <p className="mt-2 text-sm font-semibold leading-6 text-slate-700 dark:text-slate-300">
                      {selectedCaption || "Aucune légende renseignée."}
                    </p>

                    {selectedPhoto && (
                      <div className="mt-3 flex flex-wrap gap-2 text-xs font-bold text-slate-500 dark:text-slate-400">
                        <span className="rounded-full bg-white px-3 py-1.5 ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">
                          ID #{selectedPhoto.id}
                        </span>

                        <span className="rounded-full bg-white px-3 py-1.5 ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">
                          Position {selectedPhoto.position || "—"}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="rounded-[1.5rem] border border-rose-200 bg-rose-50 p-4 dark:border-rose-900/50 dark:bg-rose-950/20">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-rose-600 text-white">
                      <FaExclamationTriangle />
                    </div>

                    <div>
                      <p className="text-sm font-bold text-rose-900 dark:text-rose-200">
                        Suppression définitive
                      </p>

                      <p className="mt-1 text-xs leading-5 text-rose-700 dark:text-rose-300">
                        La photo sera supprimée de l’album. Vérifie bien
                        l’aperçu avant de confirmer.
                      </p>
                    </div>
                  </div>

                  <label className="mt-4 flex cursor-pointer items-center gap-3 rounded-2xl bg-white p-3 text-sm font-bold text-slate-800 ring-1 ring-rose-100 dark:bg-slate-900 dark:text-slate-100 dark:ring-rose-900/40">
                    <input
                      type="checkbox"
                      checked={confirmDelete}
                      onChange={(event) =>
                        setConfirmDelete(event.target.checked)
                      }
                      className="h-5 w-5 accent-rose-600"
                    />
                    Je confirme supprimer cette photo.
                  </label>
                </div>
              </section>
            </div>
          )}
        </div>

        <footer className="border-t border-slate-200 bg-white/95 px-5 py-4 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95 sm:px-6">
          <div className="grid grid-cols-2 gap-3 sm:flex sm:justify-end">
            <button
              type="button"
              onClick={safeClose}
              disabled={deleting}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-slate-100 px-5 text-sm font-bold text-slate-700 transition active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-slate-800 dark:text-slate-200"
            >
              <FaTimes />
              Annuler
            </button>

            <button
              type="button"
              onClick={handleDelete}
              disabled={!canDelete}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-rose-600 px-5 text-sm font-bold text-white transition active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaTrash />
              {deleting ? "Suppression..." : "Supprimer"}
            </button>
          </div>

          <p className="mt-3 text-center text-[11px] text-slate-500 dark:text-slate-400 sm:text-right">
            {selectedPhoto
              ? "Sélectionne une photo, coche la confirmation puis supprime."
              : "Aucune photo sélectionnée."}
          </p>
        </footer>
      </article>
    </div>,
    document.body
  );
}